import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'

import { ApiTaSf010 } from '~/endpoints/ApiTaSf010'
import { ApiTaSf030 } from '~/endpoints/ApiTaSf030'

// State types

interface SubjectNode {
  subjectId: string
  name: string
}
export interface ChoicesState {
  courseId: string
  memberGrade: number[]
  attendanceSubject: SubjectNode[]
}
const getEmptyChoicesState = (): ChoicesState => ({
  courseId: '',
  memberGrade: [],
  attendanceSubject: [],
})

export interface LearningUnitNode {
  unitSubunitId: string
  isDeliveredFlg: boolean
  deliveryMonth: string
  subunitsName: string
  effortStatus: string
  isTestPiecesFlg: boolean
  correctionsStatus: string | null
  testScore: string | null
}
interface LearningUnitsState {
  learningUnitList: LearningUnitNode[]
}
const getEmptyLearningUnitsState = (): LearningUnitsState => ({
  learningUnitList: [],
})

// State action parameter

interface ActionParamsSetChoices {
  courseId: string
  planId: string
  levelId: string
  memberGrade?: number
  teachingMaterialType?: number
}

interface ActionParamsSetLearningUnits {
  subjectId: string
  grade: number
  subunitType: number
  courseId: string
  planId?: string
}

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'apis/Learn',
})
export default class Learn extends VuexModule {
  // State

  // Choices
  private choices_ = getEmptyChoicesState()

  // LearningUnits
  private learningUnits_ = getEmptyLearningUnitsState()

  // Getters

  get choices(): ChoicesState {
    return this.choices_
  }

  get learningUnits(): LearningUnitsState {
    return this.learningUnits_
  }

  // Mutations

  @Mutation
  updateChoices(value: ChoicesState) {
    this.choices_ = value
  }

  @Mutation
  updateLearningUnits(value: LearningUnitsState) {
    this.learningUnits_ = value
  }

  @Action
  clear() {
    this.context.commit('updateChoices', getEmptyChoicesState())
    this.context.commit('updateLearningUnits', getEmptyLearningUnitsState())
  }

  @Action
  async setChoices(param: ActionParamsSetChoices) {
    logger.trace('in: setChoices')

    // API: API_TA_SF_010
    const api = new ApiTaSf010.Api()
    const result = await api.get<ChoicesState | null>(
      {
        query: {
          courseId: param.courseId,
          planId: param.planId,
          levelId: param.levelId,
          memberGrade: param.memberGrade,
          teachingMaterialType: param.teachingMaterialType,
        },
      },
      // success
      (data, _) => {
        return {
          courseId: param.courseId,
          memberGrade: data.memberGrade,
          attendanceSubject: data.attendanceSubject,
        }
      },
      // error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )

    if (result === null) {
      return
    }
    this.context.commit('updateChoices', result)
  }

  @Action
  async setLearningUnits(param: ActionParamsSetLearningUnits) {
    logger.trace('in: setLearningUnits')

    // API: API_TA_SF_030
    const api = new ApiTaSf030.Api()
    const result = await api.get<LearningUnitsState | null>(
      {
        query: {
          subjectId: param.subjectId,
          grade: param.grade,
          subunitType: param.subunitType,
          courseId: param.courseId,
          planId: param.planId,
        },
      },
      // success
      (data, _) => {
        return {
          learningUnitList: data.learningUnitList,
        }
      },
      // error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )

    if (result === null) {
      return
    }
    this.context.commit('updateLearningUnits', result)
  }

  @Action
  clearLearningUnits() {
    logger.trace('in: clearLearningUnits')

    this.context.commit('updateLearningUnits', getEmptyLearningUnitsState())
  }
}
